import React, { useMemo, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Select } from '../../../../components/ui/Select';
import type { UserRow } from './types';
import type { AccountsState } from '../../../../accounts';
import { getLinkedParents, getLearnerExistingClassrooms } from '../../../../accounts';
interface LinkUsersFormProps {
  users: UserRow[];
  accounts: AccountsState | undefined;
  store: {
    linkParentToLearnerById: (parentId: string, learnerId: string) => void;
    linkTeacherToLearnerById: (teacherId: string, learnerId: string) => void;
  };
  onClose: () => void;
}
export function LinkUsersForm({
  users,
  accounts,
  store,
  onClose
}: LinkUsersFormProps) {
  const [linkSourceId, setLinkSourceId] = useState('');
  const [linkTargetId, setLinkTargetId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  // Compute warnings when both source and target are selected
  const warnings = useMemo(() => {
    if (!linkSourceId || !linkTargetId || !accounts) return [];
    const result: Array<{
      type: 'info' | 'warning';
      message: string;
    }> = [];
    const sourceAccount = accounts.accountsById[linkSourceId];
    const targetAccount = accounts.accountsById[linkTargetId];
    if (!sourceAccount || !targetAccount) return result;
    if (sourceAccount.role === 'parent') {
      // Check if learner already has other parents
      const existingParents = getLinkedParents(accounts, linkTargetId);
      const otherParents = existingParents.filter(p => p.id !== linkSourceId);
      if (otherParents.length > 0) {
        const names = otherParents.map(p => p.displayName).join(', ');
        result.push({
          type: 'info',
          message: `${targetAccount.displayName} is al gekoppeld aan: ${names}. Deze ouder wordt als extra ouder toegevoegd.`
        });
      }
    } else if (sourceAccount.role === 'teacher') {
      // Check if learner is already in another teacher's classroom
      const existingClassrooms = getLearnerExistingClassrooms(accounts, linkTargetId);
      const otherTeacherClassrooms = existingClassrooms.filter(c => c.classroom.teacherId !== linkSourceId);
      if (otherTeacherClassrooms.length > 0) {
        const details = otherTeacherClassrooms.map(c => `'${c.classroom.name}' van ${c.teacherName}`).join(', ');
        result.push({
          type: 'info',
          message: `${targetAccount.displayName} zit al in: ${details}. Deze leerling wordt ook zichtbaar voor de nieuwe docent.`
        });
      }
    }
    return result;
  }, [linkSourceId, linkTargetId, accounts]);
  // Reset confirmation when selection changes
  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLinkSourceId(e.target.value);
    setConfirmed(false);
  };
  const handleTargetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLinkTargetId(e.target.value);
    setConfirmed(false);
  };
  const handleLinkUsers = (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkSourceId || !linkTargetId) return;
    // If there are warnings and not yet confirmed, require confirmation
    if (warnings.length > 0 && !confirmed) {
      setConfirmed(true);
      return;
    }
    const sourceAccount = accounts?.accountsById?.[linkSourceId];
    if (sourceAccount?.role === 'parent') {
      store.linkParentToLearnerById(linkSourceId, linkTargetId);
    } else if (sourceAccount?.role === 'teacher') {
      store.linkTeacherToLearnerById(linkSourceId, linkTargetId);
    }
    onClose();
  };
  return <form onSubmit={handleLinkUsers} data-id="element-6581">
      <Select label="Ouder of Docent" required value={linkSourceId} onChange={handleSourceChange} placeholder="Selecteer een ouder of docent..." options={users.filter(u => u.role === 'parent' || u.role === 'teacher').map(u => ({
      value: u.id,
      label: `${u.name} (${u.role === 'parent' ? 'Ouder' : 'Docent'})`
    }))} data-id="element-6582" />
      <Select label="Leerling" required value={linkTargetId} onChange={handleTargetChange} placeholder="Selecteer een leerling..." options={users.filter(u => u.role === 'learner').map(u => ({
      value: u.id,
      label: u.name
    }))} data-id="element-6583" />

      {/* Validation warnings */}
      {warnings.length > 0 && <div className="mt-3 space-y-2" data-id="element-6584">
          {warnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.type === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`} data-id="element-6585">
              {w.type === 'warning' ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-6586" /> : <Info className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-6587" />}
              <span data-id="element-6588">{w.message}</span>
            </div>)}
        </div>}

      <div className="pt-4 flex justify-end gap-2" data-id="element-6589">
        <Button type="button" variant="secondary" onClick={onClose} data-id="element-6590">
          Annuleren
        </Button>
        <Button type="submit" disabled={!linkSourceId || !linkTargetId} data-id="element-6591">
          {warnings.length > 0 && !confirmed ? 'Ja, toch koppelen' : 'Koppelen'}
        </Button>
      </div>
    </form>;
}